import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import AuthService from '../../repository/authRepository/AuthService';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const checkSession = useCallback(async () => {
    try {
      const sessionData = localStorage.getItem('authSession');

      if (sessionData) {
        const { user, expiry } = JSON.parse(sessionData);
        const now = new Date().getTime();

        if (now < expiry) {
          setCurrentUser(user);
          setIsAuthenticated(true);
          setIsAdmin(user.role === 'ROLE_ADMIN');
          setIsInitialized(true);
          return true;
        } else {
          // Session expired
          await logout();
          setIsInitialized(true);
          return false;
        }
      } else {
        const user = AuthService.getCurrentUser();
        if (user) {
          createSession(user);
          setIsInitialized(true);
          return true;
        }
        setIsInitialized(true);
        return false;
      }
    } catch (error) {
      console.error("Error checking session:", error);
      setIsInitialized(true);
      return false;
    }
  }, []);

  const createSession = useCallback((user) => {
    const expiryTime = new Date().getTime() + (8 * 60 * 60 * 1000);

    const sessionData = {
      user: user,
      expiry: expiryTime
    };

    localStorage.setItem('authSession', JSON.stringify(sessionData));

    setCurrentUser(user);
    setIsAuthenticated(true);
    setIsAdmin(user.role === 'ROLE_ADMIN');
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('authSession');
    await AuthService.logout();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (username, password) => {
    const user = await AuthService.login(username, password);
    createSession(user);
    return user;
  };

  const register = async (username, email, password, name, surname, dateOfBirth, phoneNumber) => {
    await AuthService.register(username, email, password, name, surname, dateOfBirth, phoneNumber);
  };

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      const refreshSession = () => {
        createSession(currentUser);
      };

      const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

      events.forEach(event => {
        window.addEventListener(event, refreshSession);
      });

      return () => {
        events.forEach(event => {
          window.removeEventListener(event, refreshSession);
        });
      };
    }
  }, [isAuthenticated, currentUser, createSession]);

  const value = {
    currentUser,
    isAuthenticated,
    isAdmin,
    isInitialized,
    login,
    logout,
    register,
    checkSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}